import type { AccessRole, DashboardShortcutTarget, DrillTarget } from '@pams/shared';
import { can } from '@pams/shared';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  A TILE'S DESTINATION, TURNED INTO THIS APP'S HREF. [P6a]
 *
 *  FR-5.5.5: every tile drills through to its rows, because "a number nobody can click is a
 *  number nobody trusts". The registry declares a DESTINATION rather than a URL — P6d's
 *  router parses no query string and holds five routes, so a dashboard URL would not be
 *  portable to the surface that renders the same registry.
 *
 *  ⚠️ THE RANGE COMES FROM WHAT THE SERVER ECHOED BACK, NEVER FROM WHAT THE CLIENT SENT.
 *  The dashboard defaults an absent range to today; a link built from the empty query string
 *  would open a list showing something else entirely, and the tile and its drill would
 *  silently disagree about which dates they are about.
 *
 *  ⚠️ `null` IS A REAL ANSWER, AND IT RENDERS AS PLAIN TEXT RATHER THAN A DEAD LINK. A role
 *  without the destination's own capability gets the number and no link — `web/src` bans
 *  `navigate(-1)` on exactly this reasoning: a control that is sometimes wrong is worse than
 *  no control.
 *
 *  ⚠️ AND THE SWITCH IS EXHAUSTIVE, so a member added to `DrillTarget` is a compile error
 *  here rather than a tile that quietly stops linking.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

export type DrillRange = { readonly from: string; readonly to: string };

const withRange = (path: string, range: DrillRange, extra: Record<string, string> = {}): string => {
  const query = new URLSearchParams({ dateFrom: range.from, dateTo: range.to, ...extra });
  return `${path}?${query.toString()}`;
};

/**
 * ⚠️ [P6b.1] EVERY DESTINATION A SHORTCUT MAY POINT AT, IN ONE TOTAL MAP.
 *
 * `DrillTarget`'s rule ("a destination, never an href") applied to the configured kind: the
 * catalogue holds a NAME and this is the one place in `web` that turns it into a path, so a
 * destination added upstream is a compile error here rather than a link that goes nowhere.
 */
const SHORTCUT_HREF: Record<DashboardShortcutTarget, string> = {
  INBOX: '/inbox',
  ARRANGEMENTS: '/arrangements',
  ARRANGEMENT_NEW: '/arrangements/new',
  STANDING_ORDERS: '/standing-orders',
  ORG_TREE: '/org-tree',
  OFFICERS: '/officers',
  PLACES: '/places',
  POLICIES: '/policies',
  POST_TYPES: '/post-types',
  VEHICLE_TYPES: '/vehicle-types',
};

export const hrefOf = (
  target: DrillTarget,
  role: AccessRole,
  range: DrillRange,
  tileId: string,
  /** ⚠️ [P6b.1] Only a `SHORTCUT` reads it, and only a layout can supply it. */
  shortcutTarget: DashboardShortcutTarget | null = null,
): string | null => {
  switch (target.kind) {
    case 'ARRANGEMENT_LIST':
      if (!can(role, 'arrangement.read')) return null;
      return withRange('/arrangements', range, target.status ? { status: target.status } : {});

    /*
     * ⚠️ `/inbox` ALREADY READS THESE KEYS. `listInboxQuerySchema` declares `dateFrom`,
     * `dateTo` and `fillState`, and `useServerTable` puts them in the URL through nuqs — so
     * six of the fourteen tiles drill correctly with no server surface and no new screen.
     * A key this app does not declare would be STRIPPED IN SILENCE ([D4.4]), which is why
     * nothing here invents one.
     */
    case 'INBOX':
      if (!can(role, 'arrangement.read')) return null;
      return withRange('/inbox', range, target.fillState ? { fillState: target.fillState } : {});

    /*
     * ⚠️ [P6b.1] NO RANGE IS APPENDED. Every other destination here carries the dates the
     * SERVER echoed back, because the list it opens should show what the number counted. A
     * shortcut counts nothing — it is a door — so a range on it would narrow a screen the
     * operator asked to see whole.
     */
    case 'SHORTCUT':
      return shortcutTarget === null ? null : SHORTCUT_HREF[shortcutTarget];

    case 'TILE_ROWS':
      return withRange(`/dashboard/tiles/${tileId}`, range);

    default: {
      const unreachable: never = target;
      return unreachable;
    }
  }
};
